import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Group,
  TextInput as MantineTextInput,
  MultiSelect,
  NumberInput,
  Paper,
  Pill,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useDeferredValue, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCreatePlaylist, useSeedPreviewPlaylist } from '~/api/mutations'
import { useLibraryFolders, useSearch } from '~/api/queries'
import type { Track } from '~/api/types'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { TextInput } from '~/components/primitives/form'
import { formatDuration } from '~/lib/format'

export const Route = createFileRoute('/_app/playlists/generate-seed')({
  component: GenerateSeedPlaylistPage,
})

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  comment: z.string().optional(),
  limit: z.number().int().min(1).max(500),
  excludeFolderIds: z.array(z.string()).optional(),
  maxSizeMB: z.number().min(0).optional(),
})

type FormData = z.infer<typeof schema>

interface SeedTrack {
  id: number
  title: string
  artist: string
}

function SeedTrackSearch({
  seeds,
  onAdd,
  onRemove,
}: {
  seeds: SeedTrack[]
  onAdd: (track: SeedTrack) => void
  onRemove: (id: number) => void
}) {
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const { data: searchData } = useSearch(deferredQuery, { limit: 10 })

  const seedIds = new Set(seeds.map((s) => s.id))
  const results = (searchData?.tracks.data ?? []).filter((t) => !seedIds.has(t.id))

  return (
    <div>
      <Text size="sm" fw={500} mb={4}>
        Seed Tracks
      </Text>
      {seeds.length > 0 && (
        <Pill.Group mb="xs">
          {seeds.map((s) => (
            <Pill key={s.id} withRemoveButton onRemove={() => onRemove(s.id)}>
              {s.artist} — {s.title}
            </Pill>
          ))}
        </Pill.Group>
      )}
      <MantineTextInput
        placeholder="Search for tracks to add as seeds..."
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
      />
      {query && results.length > 0 && (
        <Paper withBorder mt={4} p="xs" mah={200} style={{ overflowY: 'auto' }}>
          <Stack gap={2}>
            {results.map((t) => (
              <Button
                key={t.id}
                variant="subtle"
                size="compact-sm"
                justify="flex-start"
                fullWidth
                onClick={() => {
                  onAdd({ id: t.id, title: t.title, artist: t.artist })
                  setQuery('')
                }}
              >
                {t.artist} — {t.title} ({t.album})
              </Button>
            ))}
          </Stack>
        </Paper>
      )}
      {query && deferredQuery && results.length === 0 && searchData && (
        <Text size="xs" c="dimmed" mt={4}>
          No matching tracks found.
        </Text>
      )}
    </div>
  )
}

function GenerateSeedPlaylistPage() {
  const navigate = useNavigate()
  const seedPreview = useSeedPreviewPlaylist()
  const createPlaylist = useCreatePlaylist()
  const { data: foldersData } = useLibraryFolders()
  const folderOptions = (foldersData?.data ?? []).map((f) => ({
    value: String(f.id),
    label: f.name,
  }))

  const [seeds, setSeeds] = useState<SeedTrack[]>([])
  const [previewTracks, setPreviewTracks] = useState<Track[] | null>(null)
  const [savedFormData, setSavedFormData] = useState<FormData | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      comment: '',
      limit: 50,
      excludeFolderIds: [],
      maxSizeMB: undefined,
    },
  })

  const onPreview = async (data: FormData) => {
    if (seeds.length === 0) {
      notifications.show({ title: 'Error', message: 'Add at least one seed track.', color: 'red' })
      return
    }
    try {
      const body: Parameters<typeof seedPreview.mutateAsync>[0] = {
        seedTrackIds: seeds.map((s) => s.id),
        limit: data.limit,
      }
      if (data.excludeFolderIds && data.excludeFolderIds.length > 0) {
        body.excludeFolderIds = data.excludeFolderIds.map(Number)
      }
      if (data.maxSizeMB !== undefined) {
        body.maxSize = data.maxSizeMB * 1_000_000
      }
      const result = await seedPreview.mutateAsync(body)
      setSavedFormData(data)
      setPreviewTracks(result.tracks)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to preview playlist.'
      notifications.show({ title: 'Error', message, color: 'red' })
    }
  }

  const onSave = async () => {
    if (!savedFormData || !previewTracks) return
    setIsSaving(true)
    try {
      const trackIds = previewTracks.map((t) => t.id)
      const result = await createPlaylist.mutateAsync({
        name: savedFormData.name,
        comment: savedFormData.comment || undefined,
        trackIds,
      })
      notifications.show({ title: 'Playlist created', message: savedFormData.name, color: 'green' })
      navigate({
        to: '/playlists/$playlistId',
        params: { playlistId: String((result as { id: number }).id) },
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create playlist.'
      notifications.show({ title: 'Error', message, color: 'red' })
    } finally {
      setIsSaving(false)
    }
  }

  const onBackToEdit = () => {
    setPreviewTracks(null)
  }

  if (previewTracks) {
    const totalDuration = previewTracks.reduce((sum, t) => sum + t.duration, 0)
    return (
      <Stack gap="md">
        <Title order={2}>
          Preview: {previewTracks.length} tracks, {formatDuration(totalDuration)}
        </Title>
        <Group>
          <Button onClick={onSave} loading={isSaving}>
            Save Playlist
          </Button>
          <Button variant="default" onClick={onBackToEdit} disabled={isSaving}>
            Back to Edit
          </Button>
        </Group>
        <TrackTable tracks={previewTracks} />
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <Title order={2}>Generate from Seeds</Title>
      <Paper withBorder p="lg" maw={600}>
        <form onSubmit={handleSubmit(onPreview)}>
          <Stack gap="md">
            <TextInput
              name="name"
              control={control}
              label="Name"
              placeholder="My Seed Playlist"
              required
            />
            <TextInput
              name="comment"
              control={control}
              label="Description"
              placeholder="Optional description"
            />

            <Controller
              name="limit"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Track limit"
                  min={1}
                  max={500}
                  value={field.value}
                  onChange={(v) => field.onChange(typeof v === 'number' ? v : 50)}
                />
              )}
            />

            <SeedTrackSearch
              seeds={seeds}
              onAdd={(track) => setSeeds((prev) => [...prev, track])}
              onRemove={(id) => setSeeds((prev) => prev.filter((s) => s.id !== id))}
            />

            <Title order={4} mt="sm">
              Filters
            </Title>

            <Controller
              name="excludeFolderIds"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Exclude folders"
                  placeholder="None (include all)"
                  data={folderOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  clearable
                  searchable
                />
              )}
            />

            <Controller
              name="maxSizeMB"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Max file size (MB)"
                  min={0}
                  placeholder="Any"
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(typeof v === 'number' ? v : undefined)}
                />
              )}
            />

            <Button type="submit" loading={isSubmitting} mt="md">
              Preview
            </Button>
          </Stack>
        </form>
      </Paper>
    </Stack>
  )
}
